/* Generic Types */
import {OnboardingChecklistType} from "../../onboarding/checklist/OnboardingChecklistController";
import {DEFAULT_DAYS_NOTIFICATION_CONFIG} from "./NotificationSessionTypes";
import { i18n } from '../../../boot/i18n';

export interface HasShowReviewAppPopup {
  hasValidatedHisFirstSkill: boolean,
  hasShownPopup: boolean
}

export interface HomeWelcomeModalInfos {
  lastSeenScore: number | null,
  userActivityLastSeen: number | null, 
  newSongsLastSeen: number
}

export type CONFIGURATION_TYPE =
  // Admin
  'TEAM_MEMBER' | 'ACCESS_DRAFT_CATALOG' | 'SELECTED_CATALOG_TYPE' | 'AUDIO_LOGS_COLLECTION_AUTHORIZATION' |
  // Player
  'ON_NOTE_DISPLAY' | 'PLAYER_DISPLAY_MODE' |
  // Onboarding
  'ONBOARDING' |
  // User Profile
  'KEYBOARD_TYPE' | 'IS_SINGER' | 'USER_GOALS' | 'SESSION_NOTIFICATIONS' | 'SELECTED_TEACHER'| 'USER_REVIEW' | 'PIECE_COURSES' | 'HOME_WELCOME_MODAL_INFOS' | 'NOT_INTERESTING_VERSIONS' |

  'CURRENT_WEEKLY_FREE_ACTIONS' | 'CURRENT_WEEKLY_FREE_TEACHER_MESSAGES' | 'CURRENT_DAILY_FREE_ACTIONS' |

  'HAS_SHOW_REVIEW_APP_POPUP' |
  
  'LANG';

export type ConfigurationValues = { [K in CONFIGURATION_TYPE]: any }

export type AuthorizedType = 'number' | 'string' | 'boolean' | 'json';
export type ConfigurationNativeTypes = { [K in CONFIGURATION_TYPE]: AuthorizedType };
export const configurationNativeTypes: ConfigurationNativeTypes = {
  // Admin
  TEAM_MEMBER: 'string',
  ACCESS_DRAFT_CATALOG: 'boolean',
  SELECTED_CATALOG_TYPE: 'string',
  AUDIO_LOGS_COLLECTION_AUTHORIZATION: 'boolean',

  // Player
  ON_NOTE_DISPLAY: 'string',

  // Onboarding
  ONBOARDING: 'json',

  // User Profile
  KEYBOARD_TYPE: 'string',
  IS_SINGER: 'boolean',
  USER_GOALS: 'json',
  SESSION_NOTIFICATIONS: 'json',
  SELECTED_TEACHER: 'string',
  USER_REVIEW: 'json',
  PLAYER_DISPLAY_MODE: 'string',
  PIECE_COURSES: 'json',
  HOME_WELCOME_MODAL_INFOS: 'json',
  NOT_INTERESTING_VERSIONS: 'json',

  CURRENT_WEEKLY_FREE_ACTIONS: 'number',
  CURRENT_WEEKLY_FREE_TEACHER_MESSAGES: 'number',
  CURRENT_DAILY_FREE_ACTIONS: 'number',

  HAS_SHOW_REVIEW_APP_POPUP: 'json',

  LANG: 'string',
}

/* Keyboard types */ // TODO: should also add number of keys
export type KEYBOARD_TYPE = 'classic' | 'midi' | 'none' | 'none-but-will-buy';
export interface KeyboardTypeWithLabel {
  value: KEYBOARD_TYPE,
  label: String,
}

export const KeyboardTypesWithLabel: KeyboardTypeWithLabel[] = [
  {value: 'classic', label: i18n.t('userConfigurationTypes.keyboardTypesWithLabel.acousticPiano') as string},
  {value: 'midi', label: i18n.t('userConfigurationTypes.keyboardTypesWithLabel.digitalPiano') as string},
  {value: 'none', label: i18n.t('userConfigurationTypes.keyboardTypesWithLabel.noPiano') as string},
  // {value: 'none-but-will-buy', label: 'Pas de piano mais prévoit d\'en acheter un'},
];

/* Goals */
export type GoalSetting = {
  id: number,
  isSelected: boolean,
  additionalField: boolean,
  additionalInfo: string,
  label: string
};
export type SavedGoalSetting = { // If saved, means that isSelected is true
  id: number,
  additionalInfo: boolean,
}
export const availableGoals: GoalSetting[] =  [
  {id: 5, isSelected: false, additionalField: false, additionalInfo: '', label: i18n.t('userConfigurationTypes.goals.enjoyPlaying') as string},
  {id: 3, isSelected: false, additionalField: false, additionalInfo: '', label: i18n.t('userConfigurationTypes.goals.improveLevel') as string},
  {id: 7, isSelected: false, additionalField: false, additionalInfo: '', label: i18n.t('userConfigurationTypes.goals.discoverNewSongs') as string},
  {id: 4, isSelected: false, additionalField: false, additionalInfo: '', label: i18n.t('userConfigurationTypes.goals.getBackToPiano') as string},
  {id: 10, isSelected: false, additionalField: false, additionalInfo: '', label: i18n.t('userConfigurationTypes.goals.learnRhythms') as string},
  {id: 8, isSelected: false, additionalField: false, additionalInfo: '', label: i18n.t('userConfigurationTypes.goals.playSingSimultaneously') as string},
  {id: 1, isSelected: false, additionalField: true, additionalInfo: '', label: i18n.t('userConfigurationTypes.goals.learnSpecificSong') as string},
  {id: 100, isSelected: false, additionalField: true, additionalInfo: '', label: i18n.t('userConfigurationTypes.goals.other') as string},
];


export type PlayerIndicationDisplayModeOptions = 'notes' | 'fingers' | 'both'

/* Display mode types */
export type PlayerDisplayModeOptions = 'piano' | 'partition'
export interface PlayerDisplayModeOptionsLabel {
  value: PlayerDisplayModeOptions,
  label: String,
}
export const PlayerDisplayModeOptionsWithLabel: PlayerDisplayModeOptionsLabel[] = [
  {value: 'piano', label: i18n.t('userConfigurationTypes.displayModes.pianoRoll') as string},
  {value: 'partition', label: i18n.t('userConfigurationTypes.displayModes.partition') as string},
];

/* Onboarding Type */
export type OnboardingSlideName = 'news' | 'welcome' | 'teacher' | 'level' | 'levelResult' | 'goal'| 'notifications'| 'keyboard' | 'trial' | 'keyboardMidiResult' | 'askKeyboardSlide' | 'sing' | 'handSize'| 'firstChallenge' | 'playerMandatory' | 'initialEvaluation' | 'finalSlide' | 'name' | 'emailPassword' | 'signupValidation' | 'tutorialSlide' | 'selectDisplayModeSlide' | 'hasPianoSlide' | 'keyboardSlide' | 'notificationsPermissionSlide' | 'keyboardSlideResult';
export type OnboardingConfig = {
  hasFinishedFirstOnboarding: boolean,
  hasFinishedMidiOnboarding: boolean,
  hasFinishedInitialEvaluation: boolean,
  onboardingSlidesCompleted: OnboardingSlideName[],
  checklistTasksCompleted: OnboardingChecklistType[],
  hideChecklist: boolean,
  hadScoreExplanation: boolean,
  hideEvaluationBanner: boolean,
  displayTutorialBanner: boolean,
  hasFinishedPlayerOnboarding: boolean,
  endTrialModalDisplayed: boolean,
}
export const defaultOnboardingConfig: OnboardingConfig = {
  hasFinishedFirstOnboarding: false,
  hasFinishedMidiOnboarding: false,
  hasFinishedInitialEvaluation: false,
  onboardingSlidesCompleted: [],
  checklistTasksCompleted: [],
  hideChecklist: false,
  hadScoreExplanation: false,
  hideEvaluationBanner: false,
  displayTutorialBanner: false,
  hasFinishedPlayerOnboarding: false,
  endTrialModalDisplayed: false,
}
export const orderedOnboardingSlideNames: OnboardingSlideName[] = [ 'welcome', 'teacher', 'name', 'goal', 'level', 'levelResult', 'selectDisplayModeSlide', 'emailPassword', 'signupValidation', 'trial', 'notificationsPermissionSlide', 'keyboardSlide', 'keyboardSlideResult', 'hasPianoSlide', 'tutorialSlide', 'finalSlide' ];


export const defaultConfigurationValues: ConfigurationValues = {
  // Admin
  TEAM_MEMBER: 'no',
  ACCESS_DRAFT_CATALOG: false,
  SELECTED_CATALOG_TYPE: 'published',
  AUDIO_LOGS_COLLECTION_AUTHORIZATION: false,

  // Player
  ON_NOTE_DISPLAY: 'fingers',

  // Onboarding
  ONBOARDING: defaultOnboardingConfig,

  // User Profile
  KEYBOARD_TYPE: 'classic',
  IS_SINGER: false,
  USER_GOALS: [],
  SESSION_NOTIFICATIONS: DEFAULT_DAYS_NOTIFICATION_CONFIG,
  SELECTED_TEACHER: '',
  USER_REVIEW: null,
  PLAYER_DISPLAY_MODE: 'piano' as PlayerDisplayModeOptions,
  PIECE_COURSES: [],
  HOME_WELCOME_MODAL_INFOS: { lastSeenScore: null, userActivityLastSeen: null, newSongsLastSeen: 1730299113000 } as HomeWelcomeModalInfos,
  NOT_INTERESTING_VERSIONS: [],
  CURRENT_WEEKLY_FREE_ACTIONS: 0,
  CURRENT_DAILY_FREE_ACTIONS: 0,
  CURRENT_WEEKLY_FREE_TEACHER_MESSAGES: 0,

  HAS_SHOW_REVIEW_APP_POPUP: {hasValidatedHisFirstSkill: false, hasShownPopup: false},

  LANG: 'fr'
}
